import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  AppWindow,
  ArrowLeft,
  Sparkles,
  Lightbulb,
  Loader2,
  Zap,
  Brain,
  AlertCircle,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DatasetUploader } from "@/components/apps/dataset-uploader";
import type { ParsedDataset } from "@/features/apps/dataset-parser";
import {
  generateApp, createAppGenerationAttempt, type AppGenerationAttempt,
  type GenerateModel,
} from "@/features/apps/generate-app";
import { cn } from "@/lib/utils";
import { ContextualHelpLink } from "@/components/help/contextual-help-link";
import { toast } from "sonner";

export const Route = createFileRoute("/apps/new")({
  head: () => ({
    meta: [
      { title: "New App — AskBell" },
      {
        name: "description",
        content: "Upload a dataset and let AskBell build an interactive app.",
      },
    ],
  }),
  component: NewAppPage,
});

const PROMPT_SUGGESTIONS = [
  "A revenue dashboard with KPIs, monthly trend, and top accounts table.",
  "A pipeline explorer where I can filter by region and stage.",
  "A summary view showing distribution by category and a comparison chart.",
];

function NewAppPage() {
  const navigate = useNavigate();
  const [parsed, setParsed] = useState<ParsedDataset | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState<GenerateModel>("fast");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lock = useRef(false);
  const retained = useRef<{signature: string; file: File; attempt: AppGenerationAttempt} | null>(null);
  const canGenerate = !!parsed && !!file && prompt.trim().length >= 8 && !busy;

  const onGenerate = async () => {
    if (!parsed || !file || !canGenerate || lock.current) return;
    lock.current = true;
    const signature = JSON.stringify({prompt: prompt.trim(), model});
    if (!retained.current || retained.current.signature !== signature || retained.current.file !== file || retained.current.attempt.terminal) retained.current = {signature, file, attempt: createAppGenerationAttempt()};
    setBusy(true);
    setError(null);
    try {
      const result = await generateApp(
        { prompt: prompt.trim(), model, parsed },
        file, retained.current.attempt,
      );
      toast.success(`Built "${result.title}"`);
      navigate({ to: "/apps/$appId", params: { appId: result.app_id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error("Couldn't build the app", { description: msg.slice(0, 200) });
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-8">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="gap-1.5 mb-6 -ml-2"
          >
            <Link to="/apps">
              <ArrowLeft className="h-4 w-4" />
              Back to apps
            </Link>
          </Button>

          <div className="flex items-center gap-2 mb-2">
            <AppWindow className="h-5 w-5 text-primary" />
            <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
              New app
            </h1>
          </div>
          <div className="flex items-start justify-between gap-4 mb-8">
            <p className="text-ui-base text-muted-foreground">
              Upload a CSV or Excel file. AskBell will read its schema and build
              a dashboard or interactive app on top of it.
            </p>
            <ContextualHelpLink topic="app-builder" label="Help" className="shrink-0 mt-0.5" />
          </div>

          {/* Step 1: Dataset */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-5 w-5 rounded-full bg-primary text-primary-foreground text-ui-xs font-bold flex items-center justify-center">
                1
              </span>
              <h2 className="text-ui-base font-bold text-foreground">
                Pick a dataset
              </h2>
            </div>
            <div data-tour-dataset-uploader="">
              <DatasetUploader
                disabled={busy}
                onParsed={(p, f) => {
                  setParsed(p);
                  setFile(f);
                }}
                onCleared={() => {
                  setParsed(null);
                  setFile(null);
                }}
              />
            </div>
          </section>

          {/* Step 2: Prompt */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span
                className={cn(
                  "h-5 w-5 rounded-full text-ui-xs font-bold flex items-center justify-center",
                  parsed
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                2
              </span>
              <h2
                className={cn(
                  "text-ui-base font-bold",
                  parsed ? "text-foreground" : "text-muted-foreground",
                )}
              >
                Describe what you want
              </h2>
            </div>

            {!parsed ? (
              <div className="rounded-ui-md border border-dashed border-border bg-card/30 p-6 text-center text-ui-sm text-muted-foreground">
                Upload a dataset to continue.
              </div>
            ) : (
              <div className="space-y-3" data-tour-app-prompt="">
                <Textarea
                  aria-label="App description"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g. A dashboard summarizing revenue by region with a monthly trend line and a table of top accounts."
                  rows={4}
                  disabled={busy}
                  className="resize-none bg-card border-border focus-visible:ring-primary/30"
                />
                <div className="flex items-start gap-2 text-ui-sm text-muted-foreground">
                  <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/70" />
                  <div className="flex flex-wrap gap-1.5">
                    {PROMPT_SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setPrompt(s)}
                        disabled={busy}
                        className="rounded-full border border-border bg-card/60 px-2.5 py-1 text-ui-xs text-foreground/80 hover:border-primary/40 hover:text-foreground transition-colors disabled:opacity-50"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Step 3: Generate */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span
                className={cn(
                  "h-5 w-5 rounded-full text-ui-xs font-bold flex items-center justify-center",
                  canGenerate
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                3
              </span>
              <h2
                className={cn(
                  "text-ui-base font-bold",
                  canGenerate ? "text-foreground" : "text-muted-foreground",
                )}
              >
                Generate
              </h2>
            </div>

            {parsed && (
              <div className="mb-3">
                <p className="text-ui-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                  Model
                </p>
                <div className="inline-flex rounded-ui-md border border-border bg-card p-0.5" data-tour-app-model-toggle="">
                  <ModelButton
                    active={model === "fast"}
                    onClick={() => setModel("fast")}
                    icon={<Zap className="h-3.5 w-3.5" />}
                    label="Fast"
                    sub="Quick drafts"
                    disabled={busy}
                  />
                  <ModelButton
                    active={model === "smart"}
                    onClick={() => setModel("smart")}
                    icon={<Brain className="h-3.5 w-3.5" />}
                    label="Smart"
                    sub="Complex apps"
                    disabled={busy}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="mb-3 flex items-start gap-2 rounded-ui-sm border border-destructive/40 bg-destructive/5 p-3 text-ui-sm text-destructive">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <pre className="whitespace-pre-wrap font-mono text-ui-xs leading-relaxed">
                  {error}
                </pre>
              </div>
            )}

            <Button
              disabled={!canGenerate}
              onClick={onGenerate}
              className="gap-1.5"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Building app…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {error ? (retained.current?.attempt.terminal ? "Start new generation" : "Retry app") : "Generate app"}
                </>
              )}
            </Button>
            {parsed && !busy && prompt.trim().length < 8 && (
              <p className="mt-2 text-ui-xs text-muted-foreground">
                Add a short description (at least 8 characters) to enable Generate.
              </p>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}

function ModelButton({
  active,
  onClick,
  icon,
  label,
  sub,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  sub: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 rounded-ui-sm px-3 py-1.5 text-ui-sm transition-colors disabled:opacity-50",
        active
          ? "bg-primary/15 text-primary"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      <span className="font-bold">{label}</span>
      <span className="text-ui-xs opacity-70">{sub}</span>
    </button>
  );
}
